import React, { useState } from 'react';
import { Phone, Mail, MapPin, Menu, X, Home, Wrench, Hammer, Zap, Droplet, PaintBucket, Square, CheckCircle } from 'lucide-react';

export default function SAFurnitureWebsite() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState('');

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');

    try {
      // Method 1: Using EmailJS (Recommended)
      // You'll need to set up EmailJS account and replace these IDs
      const response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          service_id: 'service_x4d0e96', // Replace with your EmailJS service ID
          template_id: 'template_d4y5la2', // Replace with your EmailJS template ID
          user_id: 'ghsv5siYfzBP12yUT', // Replace with your EmailJS public key
          template_params: {
            from_name: formData.name,
            from_email: formData.email,
            phone: formData.phone,
            service: formData.service,
            message: formData.message,
            email: 'shahjadahmed33376@gmail.com'
          }
        })
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', phone: '', email: '', service: '', message: '' });
        setTimeout(() => setFormStatus(''), 5000);
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      console.error('Error:', error);
      setFormStatus('error');
    }
  };

  const services = [
    {
      icon: <Home className="w-8 h-8" />,
      title: "Construction from Scratch",
      description: "Complete building construction services with expert planning and execution"
    },
    {
      icon: <Hammer className="w-8 h-8" />,
      title: "Custom Furniture",
      description: "Bespoke furniture design and manufacturing for residential and commercial spaces"
    },
    {
      icon: <PaintBucket className="w-8 h-8" />,
      title: "POP & False Ceiling",
      description: "Beautiful plaster of Paris work and modern false ceiling designs"
    },
    {
      icon: <Square className="w-8 h-8" />,
      title: "Flooring & Tiling",
      description: "Premium quality flooring solutions including tiles, marble, and wooden flooring"
    },
    {
      icon: <Droplet className="w-8 h-8" />,
      title: "Sanitary Work",
      description: "Complete bathroom fitting, plumbing, and sanitary installation services"
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Electrical Work",
      description: "Professional electrical wiring, fitting, and maintenance services"
    }
  ];

  const whyChooseUs = [
    "40+ Years of Experience",
    "Expert Craftmanship",
    "Timely Project Delivery",
    "Quality Materials",
    "Competitive Pricing",
    "End-to-End Solutions"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-md fixed w-full top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Wrench className="w-8 h-8 text-orange-600" />
              <span className="ml-2 text-2xl font-bold text-gray-900">SA Furniture</span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-8">
              <a href="#home" className="text-gray-700 hover:text-orange-600 transition">Home</a>
              <a href="#services" className="text-gray-700 hover:text-orange-600 transition">Services</a>
              <a href="#about" className="text-gray-700 hover:text-orange-600 transition">About</a>
              <a href="#contact" className="text-gray-700 hover:text-orange-600 transition">Contact</a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4">
              <a href="#home" className="block py-2 text-gray-700 hover:text-orange-600">Home</a>
              <a href="#services" className="block py-2 text-gray-700 hover:text-orange-600">Services</a>
              <a href="#about" className="block py-2 text-gray-700 hover:text-orange-600">About</a>
              <a href="#contact" className="block py-2 text-gray-700 hover:text-orange-600">Contact</a>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="pt-20 bg-gradient-to-br from-orange-50 to-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Building Your Dreams<br />
              <span className="text-orange-600">One Project at a Time</span>
            </h1>
            <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
              Complete building decoration and construction services in Delhi. From furniture to full construction, we deliver excellence in every detail.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#contact" className="bg-orange-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-orange-700 transition">
                Get Free Quote
              </a>
              <a href="tel:9811108289" className="bg-white text-orange-600 px-8 py-3 rounded-lg font-semibold border-2 border-orange-600 hover:bg-orange-50 transition">
                Call Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-xl text-gray-600">Comprehensive building solutions for all your needs</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-xl hover:shadow-xl transition transform hover:-translate-y-1">
                <div className="text-orange-600 mb-4">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">About SA Furniture</h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                Led by <strong>Shahjad Ahmad</strong>, SA Furniture has been a trusted name in building decoration and construction services in Delhi. With decades of experience and a commitment to quality, we transform spaces into beautiful, functional environments.
              </p>
              <p className="text-gray-700 mb-6 leading-relaxed">
                From complete building construction to specialized furniture work, we handle every aspect of your project with professionalism and attention to detail. Our team of skilled craftsmen ensures that every project meets the highest standards of quality and durability.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {whyChooseUs.map((reason, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">{reason}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-gradient-to-br from-orange-600 to-orange-400 p-8 rounded-2xl text-white">
              <h3 className="text-2xl font-bold mb-6">Our Expertise</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="text-3xl mr-3">🏗️</span>
                  <div>
                    <strong>Full Construction</strong>
                    <p className="text-orange-100">Complete building projects from foundation to finishing</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-3xl mr-3">🪑</span>
                  <div>
                    <strong>Custom Furniture</strong>
                    <p className="text-orange-100">Tailored furniture solutions for every space</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-3xl mr-3">✨</span>
                  <div>
                    <strong>Interior Finishing</strong>
                    <p className="text-orange-100">POP, tiling, flooring, and complete interior work</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="text-3xl mr-3">⚡</span>
                  <div>
                    <strong>Technical Services</strong>
                    <p className="text-orange-100">Electrical, plumbing, and sanitary installations</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Get In Touch</h2>
            <p className="text-xl text-gray-600">Ready to start your project? Contact us today!</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-8 rounded-xl text-center">
              <Phone className="w-12 h-12 text-orange-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Phone</h3>
              <a href="tel:9811108289" className="text-orange-600 font-semibold text-lg hover:underline">
                9811108289
              </a>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-8 rounded-xl text-center">
              <Mail className="w-12 h-12 text-orange-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Email</h3>
              <a href="mailto:shahjadahmed33376@gmail.com" className="text-orange-600 font-semibold hover:underline break-all">
                shahjadahmed33376@gmail.com
              </a>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-8 rounded-xl text-center">
              <MapPin className="w-12 h-12 text-orange-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">Workshop</h3>
              <p className="text-gray-700">
                D-56/2 Vijay Vihar Ph-2<br />
                Street No 9, Rohini Sector-4<br />
                Delhi, 110085
              </p>
            </div>
          </div>

          <div className="mt-12 bg-gray-50 p-8 rounded-xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Request a Quote</h3>
            
            {formStatus === 'success' && (
              <div className="mb-4 p-4 bg-green-100 text-green-700 rounded-lg text-center">
                ✅ Thank you! Your request has been sent successfully. We'll contact you soon!
              </div>
            )}
            
            {formStatus === 'error' && (
              <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg text-center">
                ❌ Sorry, something went wrong. Please call us at 9811108289 or email directly.
              </div>
            )}

            <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <input 
                  type="text" 
                  name="name"
                  placeholder="Your Name" 
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-500 focus:outline-none"
                />
                <input 
                  type="tel" 
                  name="phone"
                  placeholder="Phone Number" 
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-500 focus:outline-none"
                />
              </div>
              <input 
                type="email" 
                name="email"
                placeholder="Email Address" 
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-500 focus:outline-none"
              />
              <select 
                name="service"
                value={formData.service}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-500 focus:outline-none"
              >
                <option value="">Select Service</option>
                <option value="Construction from Scratch">Construction from Scratch</option>
                <option value="Custom Furniture">Custom Furniture</option>
                <option value="POP & False Ceiling">POP & False Ceiling</option>
                <option value="Flooring & Tiling">Flooring & Tiling</option>
                <option value="Sanitary Work">Sanitary Work</option>
                <option value="Electrical Work">Electrical Work</option>
                <option value="Multiple Services">Multiple Services</option>
              </select>
              <textarea 
                name="message"
                placeholder="Project Details" 
                rows="4"
                value={formData.message}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-500 focus:outline-none"
              ></textarea>
              <button 
                type="submit" 
                disabled={formStatus === 'sending'}
                className="w-full bg-orange-600 text-white py-3 rounded-lg font-semibold hover:bg-orange-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {formStatus === 'sending' ? 'Sending...' : 'Submit Request'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <Wrench className="w-8 h-8 text-orange-600" />
                <span className="ml-2 text-2xl font-bold">SA Furniture</span>
              </div>
              <p className="text-gray-400">
                Your trusted partner for complete building decoration and construction services in Delhi.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><a href="#home" className="text-gray-400 hover:text-orange-600 transition">Home</a></li>
                <li><a href="#services" className="text-gray-400 hover:text-orange-600 transition">Services</a></li>
                <li><a href="#about" className="text-gray-400 hover:text-orange-600 transition">About</a></li>
                <li><a href="#contact" className="text-gray-400 hover:text-orange-600 transition">Contact</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Contact Info</h4>
              <ul className="space-y-2 text-gray-400">
                <li>📞 9811108289</li>
                <li>✉️ shahjadahmed33376@gmail.com</li>
                <li>📍 D-56/2 Vijay Vihar Ph-2, Rohini Sector-4, Delhi 110085</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 SA Furniture. All rights reserved. | Building Dreams, Crafting Excellence</p>
          </div>
        </div>
      </footer>
    </div>
  );
}